/**
 * Core game types for Pah-peh-lee-tohs.
 * Shared between client and backend.
 */

// --- Card & Deck Types ---

export type CardCategory =
  | 'Movies'
  | 'Music'
  | 'Sports'
  | 'History'
  | 'Science'
  | 'Pop Culture'
  | 'Food & Drink'
  | 'Geography'
  | 'Custom';

export type CardPoints = 1 | 2 | 3 | 4;

export interface Card {
  id: string;
  category: CardCategory;
  title: string; // max 80 characters
  description: string; // max 200 characters
  points: CardPoints;
}

export interface Deck {
  id: string;
  name: string;
  description: string;
  cards: Card[];
}

// --- Session & Player Types ---

export type SessionStatus = 'lobby' | 'setup' | 'playing' | 'ended';
export type Team = 'A' | 'B';
export type PlayerOrder = 'shuffle' | 'define';

export interface Player {
  id: string;
  displayName: string; // 1-20 characters
  team: Team | null;
  isHost: boolean;
}

/** A connected device in a multi-device session */
export interface ConnectedDevice {
  connectionId: string;
  role: DeviceRole;
  teamAffinity: ScoreboardAffinity; // For 3+ device mode: which team's scoreboard
  isHost: boolean;
  label: string; // e.g., "Reader", "Scoreboard", "Device 2"
}

export type CardsPerPlayer = 4 | 5 | 6 | 7;

export interface GameSettings {
  timerSeconds: 30 | 45 | 60 | 90;
  rounds: 3 | 4;
  playerOrder: PlayerOrder;
  drawingRoundEnabled: boolean;
  deckId: string;
  cardsPerPlayer: CardsPerPlayer;
}

// --- Game State Types ---

export type RoundNumber = 1 | 2 | 3 | 4;

export type RoundType = 'describe' | 'one-word' | 'act' | 'draw';

export const ROUND_RULES: Record<RoundNumber, { type: RoundType; name: string; description: string }> = {
  1: {
    type: 'describe',
    name: 'Describe It',
    description: 'Use any words to describe the card — but you CANNOT say any word in the title!',
  },
  2: {
    type: 'one-word',
    name: 'One Word',
    description: 'Say exactly ONE word to get your team to guess. No gestures!',
  },
  3: {
    type: 'act',
    name: 'Act It Out',
    description: 'Use only physical acting. No spoken words allowed!',
  },
  4: {
    type: 'draw',
    name: 'Draw It',
    description: 'Draw the card title on screen. No letters or numbers!',
  },
};

export interface TeamScores {
  A: number;
  B: number;
}

export interface GameState {
  sessionId: string;
  status: SessionStatus;
  players: Player[];
  settings: GameSettings;
  currentRound: RoundNumber;
  currentTeam: Team;
  currentCardReaderId: string;
  turnOrder: { A: string[]; B: string[] };
  turnIndex: { A: number; B: number };
  scores: TeamScores;
  roundScores: Record<RoundNumber, TeamScores>;
  deck: Card[];
  playedCardIds: string[];
  currentCardIndex: number;
  timerStartedAt: number | null;
  timerRemaining: number;
}

// --- Protest Types ---

export type ProtestStatus = 'pending' | 'upheld' | 'dismissed';

export interface ProtestVote {
  team: Team;
  vote: 'valid' | 'invalid';
}

export interface Protest {
  id: string;
  cardTitle: string;
  cardPoints: CardPoints;
  status: ProtestStatus;
  votes: ProtestVote[];
  createdAt: number;
}

// --- Play Mode ---

export type PlayMode = 'single-device' | 'multi-device';

// --- Device Role (multi-device) ---

/** Device roles in the new model: Reader shows cards, Scoreboard shows timer/scores/controls */
export type DeviceRole = 'reader' | 'scoreboard' | 'reader+scoreboard';

/** Optional team affinity for Scoreboard devices (enables protest button for inactive team) */
export type ScoreboardAffinity = Team | null;

// --- Validation Constants ---

export const VALIDATION = {
  DISPLAY_NAME_MIN: 1,
  DISPLAY_NAME_MAX: 20,
  CARD_TITLE_MAX: 80,
  CARD_DESCRIPTION_MAX: 200,
  CARD_POINTS_MIN: 1,
  CARD_POINTS_MAX: 4,
  MIN_CUSTOM_DECK_CARDS: 10,
  MIN_PLAYERS: 4,
  MAX_PLAYERS: 20,
  MIN_PLAYERS_PER_TEAM: 2,
  MAX_DEVICES: 10,
  SESSION_CODE_LENGTH: 4,
  SESSION_TTL_SECONDS: 3600,
  LOBBY_TIMEOUT_MINUTES: 30,
  PROTEST_WINDOW_SECONDS: 5,
  PROTEST_TIMEOUT_SECONDS: 30,
  RECONNECT_MAX_RETRIES: 5,
  RECONNECT_MAX_SECONDS: 30,
  DISCONNECT_REMOVAL_SECONDS: 60,
} as const;
